import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Loader2 } from 'lucide-react';
import { createPhysicalAssessment } from '@/lib/api';

export default function CreateAssessment() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    system: '',
    type: 'normal' as 'normal' | 'abnormal' | 'critical',
    description: '',
    
    // Vital Signs
    includeVitals: false,
    bp: '',
    hr: '',
    rr: '',
    temp: '',
    o2sat: '',
    
    // Physical Examination Components
    generalAppearance: '',
    inspection: '',
    palpation: '',
    percussion: '',
    auscultation: '',
    
    // Additional Findings
    findings: [] as string[],
    findingInput: '',
    
    // Clinical Significance
    clinicalSignificance: '',
    differentialConsiderations: '',
    recommendedActions: '',
    teachingPoints: '',
  });

  const handleAddFinding = () => {
    if (formData.findingInput.trim()) {
      setFormData({
        ...formData,
        findings: [...formData.findings, formData.findingInput.trim()],
        findingInput: '',
      });
    }
  };

  const handleRemoveFinding = (index: number) => {
    setFormData({
      ...formData,
      findings: formData.findings.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await createPhysicalAssessment({
      name: formData.name,
      system: formData.system,
      type: formData.type,
      description: formData.description,
      include_vitals: formData.includeVitals,
      bp: formData.bp,
      hr: formData.hr,
      rr: formData.rr,
      temp: formData.temp,
      o2sat: formData.o2sat,
      general_appearance: formData.generalAppearance,
      inspection: formData.inspection,
      palpation: formData.palpation,
      percussion: formData.percussion,
      auscultation: formData.auscultation,
      findings: formData.findings,
      clinical_significance: formData.clinicalSignificance,
      differential_considerations: formData.differentialConsiderations,
      recommended_actions: formData.recommendedActions,
      teaching_points: formData.teachingPoints,
    });

    setIsSubmitting(false);

    if (result.success) {
      navigate('/admin/assessments');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/assessments')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#024223] mb-4"
          >
            <ArrowLeft size={20} />
            Back to Assessments
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Add Physical Assessment</h1>
          <p className="text-lg text-gray-600">
            Create a new physical examination assessment for patient simulations
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assessment Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Normal Cardiovascular Examination"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body System *
                </label>
                <select
                  required
                  value={formData.system}
                  onChange={(e) => setFormData({...formData, system: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                >
                  <option value="">Select system</option>
                  <option value="general_constitutional">General / Constitutional</option>
                  <option value="heent">HEENT (Head/Eyes/Ears/Nose/Throat)</option>
                  <option value="neck">Neck (Nodes/Thyroid)</option>
                  <option value="cardiovascular">Cardiovascular</option>
                  <option value="respiratory">Respiratory</option>
                  <option value="abdominal_gi">Abdominal / GI</option>
                  <option value="musculoskeletal">Musculoskeletal</option>
                  <option value="neurological">Neurological</option>
                  <option value="skin">Skin</option>
                  <option value="psych_mental">Psych / Mental State</option>
                  <option value="genitourinary">Genitourinary</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Finding Type *
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                >
                  <option value="normal">✓ Normal Findings</option>
                  <option value="abnormal">⚠ Abnormal Findings</option>
                  <option value="critical">⚠ Critical Findings</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  placeholder="Brief description of this assessment scenario..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Vital Signs */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Vital Signs</h2>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.includeVitals}
                  onChange={(e) => setFormData({...formData, includeVitals: e.target.checked})}
                  className="w-4 h-4 text-[#024223] focus:ring-[#024223] border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Include vital signs</span>
              </label>
            </div>
            
            {formData.includeVitals && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Pressure (BP)
                  </label>
                  <input
                    type="text"
                    value={formData.bp}
                    onChange={(e) => setFormData({...formData, bp: e.target.value})}
                    placeholder="120/80 mmHg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heart Rate (HR)
                  </label>
                  <input
                    type="text"
                    value={formData.hr}
                    onChange={(e) => setFormData({...formData, hr: e.target.value})}
                    placeholder="72 bpm"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Respiratory Rate (RR)
                  </label>
                  <input
                    type="text"
                    value={formData.rr}
                    onChange={(e) => setFormData({...formData, rr: e.target.value})}
                    placeholder="16 breaths/min"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature
                  </label>
                  <input
                    type="text"
                    value={formData.temp}
                    onChange={(e) => setFormData({...formData, temp: e.target.value})}
                    placeholder="37.0°C"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Oxygen Saturation (SpO₂)
                  </label>
                  <input
                    type="text"
                    value={formData.o2sat}
                    onChange={(e) => setFormData({...formData, o2sat: e.target.value})}
                    placeholder="98%"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Physical Examination Components */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Physical Examination</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  General Appearance
                </label>
                <textarea
                  value={formData.generalAppearance}
                  onChange={(e) => setFormData({...formData, generalAppearance: e.target.value})}
                  rows={3}
                  placeholder="Patient appears comfortable, well-nourished, in no acute distress..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inspection
                </label>
                <textarea
                  value={formData.inspection}
                  onChange={(e) => setFormData({...formData, inspection: e.target.value})}
                  rows={3}
                  placeholder="Visual examination findings..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Palpation
                </label>
                <textarea
                  value={formData.palpation}
                  onChange={(e) => setFormData({...formData, palpation: e.target.value})}
                  rows={3}
                  placeholder="Palpation findings..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Percussion
                </label>
                <textarea
                  value={formData.percussion}
                  onChange={(e) => setFormData({...formData, percussion: e.target.value})}
                  rows={3}
                  placeholder="Percussion findings..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auscultation
                </label>
                <textarea
                  value={formData.auscultation}
                  onChange={(e) => setFormData({...formData, auscultation: e.target.value})}
                  rows={3}
                  placeholder="Auscultation findings..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Key Findings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Key Findings</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Specific Findings
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.findingInput}
                  onChange={(e) => setFormData({...formData, findingInput: e.target.value})}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFinding())}
                  placeholder="e.g., Regular heart rhythm, No murmurs"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddFinding}
                  className="px-4 py-2 bg-[#024223] text-white rounded-lg hover:bg-[#035A2F] transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {formData.findings.length > 0 && (
              <div className="space-y-2">
                {formData.findings.map((finding, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-[#E8F5F0] rounded-lg"
                  >
                    <span className="text-sm text-gray-900">{finding}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFinding(idx)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Clinical Significance */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Clinical Significance</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clinical Significance
                </label>
                <textarea
                  value={formData.clinicalSignificance}
                  onChange={(e) => setFormData({...formData, clinicalSignificance: e.target.value})}
                  rows={4}
                  placeholder="Explain the clinical significance of these findings..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Differential Considerations
                </label>
                <textarea
                  value={formData.differentialConsiderations}
                  onChange={(e) => setFormData({...formData, differentialConsiderations: e.target.value})}
                  rows={4}
                  placeholder="What conditions should be considered based on these findings..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recommended Actions
                </label>
                <textarea
                  value={formData.recommendedActions}
                  onChange={(e) => setFormData({...formData, recommendedActions: e.target.value})}
                  rows={4}
                  placeholder="Recommended next steps or interventions..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teaching Points
                </label>
                <textarea
                  value={formData.teachingPoints}
                  onChange={(e) => setFormData({...formData, teachingPoints: e.target.value})}
                  rows={4}
                  placeholder="Key teaching points for this assessment..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-[#024223] text-white font-medium rounded-lg hover:bg-[#035A2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Assessment'
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/assessments')}
              className="px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
